import { useTranslation } from 'react-i18next';
import {
  LEGACY_RANGE_PRESETS,
  resolvePreset,
  formatDisplayDate,
  isSameDay,
  type LegacyRangePresetId,
} from '../lib/date-range';

/**
 * The preset column beside the calendar. Each card shows its label above the
 * dates it currently resolves to — so "This Week" reads "03.08.2026 -
 * 05.08.2026" and the merchant can see what they are about to apply.
 *
 * Cards are 248×62 at a 16px radius, grey when idle and brand orange when
 * active (measured off the reference). A single-day preset renders one date,
 * not a range collapsed onto itself.
 */
interface LegacyRangePresetsProps {
  active: LegacyRangePresetId | null;
  /** "Today" for resolution — injected so the panel is testable. */
  today?: Date;
  onSelect: (preset: LegacyRangePresetId) => void;
}

export function LegacyRangePresets({
  active,
  today = new Date(),
  onSelect,
}: LegacyRangePresetsProps) {
  const { t } = useTranslation('legacy');

  return (
    <div className="flex w-[296px] max-w-full shrink-0 flex-col gap-2 rounded-[24px] bg-[var(--lg-surface)] p-6">
      {LEGACY_RANGE_PRESETS.map((preset) => {
        const range = resolvePreset(preset, today);
        const isActive = preset === active;
        const isSingleDay = isSameDay(range.from, range.to);

        return (
          <button
            key={preset}
            type="button"
            onClick={() => onSelect(preset)}
            aria-pressed={isActive}
            className={[
              'flex h-[62px] w-full cursor-pointer flex-col justify-center gap-0.5 rounded-[16px] px-4 text-left transition-colors',
              isActive
                ? 'bg-[var(--lg-orange-500)] text-[var(--lg-surface)]'
                : 'bg-[var(--lg-grey-100)] text-[var(--lg-fg)] hover:bg-[var(--lg-grey-200)]',
            ].join(' ')}
          >
            <span className="text-[16px] font-medium">{t(`date.${preset}`)}</span>
            <span
              className={[
                'text-[14px]',
                isActive ? 'text-[var(--lg-surface)]/90' : 'text-[var(--lg-grey-500)]',
              ].join(' ')}
            >
              {isSingleDay
                ? formatDisplayDate(range.from)
                : `${formatDisplayDate(range.from)} - ${formatDisplayDate(range.to)}`}
            </span>
          </button>
        );
      })}
    </div>
  );
}
